import { HiChevronRight } from "react-icons/hi";
import { MdOutlineInbox, MdSearch, MdFilterList } from "react-icons/md";
import { TbDownload, TbUpload } from "react-icons/tb";
import { RiDeleteBinLine, RiEditLine } from "react-icons/ri";
import { FiUsers, FiPlus } from "react-icons/fi";
import AddContactModal from "./AddContactModal";
import ImportContactModal from "../ImportContactModal";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import apiEndpoints from "../../apiconfig";
import { useSnackbar } from "../../DynamicComponents/SnackbarContext";
import { MdBlock } from "react-icons/md";

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const navigate = useNavigate();

  // Get snackbar context
  const { showSnackbar } = useSnackbar();

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isAddModalOpen || isImportModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isAddModalOpen, isImportModalOpen]);

  const fetchContacts = async () => {
    try {
      const token = getToken();
      const response = await axios.get(apiEndpoints.addContact, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      let data = response.data;
      if (data && typeof data === "object" && data.data) data = data.data;
      if (!Array.isArray(data)) data = [];
      setContacts(data);
      setFilteredContacts(data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setContacts([]);
      setFilteredContacts([]);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredContacts(contacts);
    } else {
      const filtered = contacts.filter(
        (contact) =>
          contact.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          contact.contact_group?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          contact.tags?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          contact.mobile_number?.includes(searchQuery)
      );
      setFilteredContacts(filtered);
    }
  }, [searchQuery, contacts]);

  const confirmDeleteAction = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);

    try {
      const token = getToken();
      await axios.delete(apiEndpoints.addContact, {
        data: { id },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      fetchContacts();
      showSnackbar("Contact deleted successfully", "success");
    } catch (err) {
      showSnackbar("Failed to delete contact", "error");
    }
  };

  // Confirm bulk delete
  const confirmBulkDeleteAction = async () => {
    setConfirmBulkDelete(false);

    try {
      const token = getToken();
      await axios.delete(apiEndpoints.addContact, {
        data: { ids: selectedContacts },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSelectedContacts([]);
      fetchContacts();
      showSnackbar("Contacts deleted successfully", "success");
    } catch (err) {
      showSnackbar("Failed to delete contacts", "error");
    }
  };

  const handleDelete = (id) => {
    setConfirmDeleteId(id); // open modal
  };

  const handleBulkDelete = () => {
    if (selectedContacts.length === 0) {
      showSnackbar("Select at least one contact.", "warning");
      return;
    }
    setConfirmBulkDelete(true); // open modal
  };

const handleBlockContact = async (contact) => {
  const token = getToken();

  try {
    if (contact.is_blocked) {
      // 🔓 UNBLOCK
      await axios.post(
        apiEndpoints.addContact,
        {
          action: "unblock_contact",
          id: contact.id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      showSnackbar("Contact unblocked successfully", "success");
    } else {
      // 🚫 BLOCK
      await axios.post(
        apiEndpoints.addContact,
        {
          action: "block_contact",
          id: contact.id,
          reason: "Blocked from contacts",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      showSnackbar("Contact blocked successfully", "success");
    }

    fetchContacts(); // refresh list
  } catch (err) {
    showSnackbar("Action failed. Please try again.", "error");
  }
};



  const handleImport = async (importedContacts) => {
    try {
      const token = getToken();
      const response = await axios.post(apiEndpoints.addContact, {
        action: "import",
        contacts: importedContacts
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.status) {
        fetchContacts();
        setIsImportModalOpen(false);
        // Show success snackbar
        showSnackbar("Contacts imported successfully", "success");
      } else {
        // Show error snackbar if import failed
        showSnackbar(response.data.message || "Failed to import contacts", "error");
      }
    } catch (error) {
      console.error("Error importing contacts:", error);
      // Show error snackbar
      showSnackbar("Failed to import contacts", "error");
    }
  };

  const handleExport = () => {
    try {
      const headers = ["Contact Name", "Group Name", "Tags", "Mobile Number"];
      const csvContent = [
        headers.join(","),
        ...filteredContacts.map(
          (c) =>
            `"${c.contact_name || ''}","${c.contact_group || ''}","${c.tags || ''}","${c.mobile_number || ''}"`
        ),
      ].join("\n");
      saveAs(new Blob([csvContent], { type: "text/csv;charset=utf-8" }), "contacts_export.csv");
      // Show success snackbar
      showSnackbar("Contacts exported successfully", "success");
    } catch (error) {
      console.error("Error exporting contacts:", error);
      // Show error snackbar
      showSnackbar("Failed to export contacts", "error");
    }
  };

  const handleExportOriginal = () => {
    try {
      const headers = ["Contact Name", "Group Name", "Tags", "Mobile Number"];
      const csvContent = [
        headers.join(","),
        ...contacts.map(
          (c) =>
            `"${c.contact_name || ''}","${c.contact_group || ''}","${c.tags || ''}","${c.mobile_number || ''}"`
        ),
      ].join("\n");
      saveAs(new Blob([csvContent], { type: "text/csv;charset=utf-8" }), "contacts_export.csv");
      // Show success snackbar
      showSnackbar("All contacts exported successfully", "success");
    } catch (error) {
      console.error("Error exporting contacts:", error);
      // Show error snackbar
      showSnackbar("Failed to export contacts", "error");
    }
  };

  const toggleSelectAll = (e) => {
    setSelectedContacts(e.target.checked ? filteredContacts.map((c) => c.id) : []);
  };

  const toggleSelectContact = (id, checked) => {
    setSelectedContacts(
      checked
        ? [...selectedContacts, id]
        : selectedContacts.filter((i) => i !== id)
    );
  };

  const handleSearch = (e) => setSearchQuery(e.target.value);
  const clearSearch = () => setSearchQuery("");

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setEditingContact(null);
  };

  const handleCloseImportModal = () => {
    setIsImportModalOpen(false);
  };

  // Function to handle successful contact addition/editing from modal
  const handleContactActionSuccess = (action, isEdit = false) => {
    fetchContacts();
    if (isEdit) {
      showSnackbar("Contact updated successfully", "success");
    } else {
      showSnackbar("Contact added successfully", "success");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-['Montserrat'] p-3 sm:p-4 md:p-1">
      {/* Header Section */}
      <div className="mb-4 sm:mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 sm:mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1">
              Contacts
            </h1>
            <div className="flex items-center text-xs sm:text-sm text-gray-500">
              <span className="text-gray-600">Home</span>
              <HiChevronRight className="mx-1 text-gray-400" />
              <span className="text-[#25D366] font-medium">Contacts</span>
            </div>
          </div>
        </div>

        {/* Action Buttons - Updated for mobile */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center justify-center bg-[#25D366] hover:bg-[#1cb85c] text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium shadow-sm transition-all duration-200 hover:shadow-md w-full xs:w-auto"
          >
            <FiPlus className="mr-1 sm:mr-2 text-sm sm:text-lg" /> Add Contact
          </button>
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="flex items-center justify-center bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium shadow-sm transition-all duration-200 hover:shadow-md w-full xs:w-auto"
          >
            <TbUpload className="mr-1 sm:mr-2" /> Import Contacts
          </button>
          <button
            onClick={handleExport}
            className="flex items-center justify-center bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium shadow-sm transition-all duration-200 hover:shadow-md w-full xs:w-auto"
          >
            <TbDownload className="mr-1 sm:mr-2" /> Export
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={selectedContacts.length === 0}
            className={`flex items-center justify-center px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium shadow-sm transition-all duration-200 w-full xs:w-auto ${
              selectedContacts.length === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-red-50 hover:bg-red-100 text-red-700 hover:text-red-800 border border-red-200"
            }`}
          >
            <RiDeleteBinLine className="mr-1 sm:mr-2" /> Delete Selected
          </button>
          <button
            onClick={() => navigate("/manage-groups")}
            className="flex items-center justify-center bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium shadow-sm transition-all duration-200 hover:shadow-md w-full xs:w-auto"
          >
            <FiUsers className="mr-1 sm:mr-2" /> Manage Groups
          </button>
        </div>
      </div>

      {/* Search & Filter Section */}
      <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 md:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4">
          <div className="flex flex-row xs:flex-row gap-2 sm:gap-3 w-full lg:w-auto">
            {/* Filter Dropdown */}
            <div className="relative w-full xs:w-32 sm:w-40">
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="appearance-none border border-gray-300 rounded-lg py-2 sm:py-2.5 pl-3 sm:pl-4 pr-8 sm:pr-10 focus:ring-2 focus:ring-[#25D366] focus:border-[#25D366] outline-none w-full bg-white text-xs sm:text-sm"
              >
                <option value="All">All Contacts</option>
                <option value="Recent">Recently Added</option>
              </select>
              <MdFilterList className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-base sm:text-lg" />
            </div>

            {/* Search Bar */}
            <div className="relative flex-1 min-w-0">
              <MdSearch className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg sm:text-xl" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={handleSearch}
                className="w-full border border-gray-300 rounded-lg py-2 sm:py-2.5 pl-8 sm:pl-10 pr-8 sm:pr-10 focus:ring-2 focus:ring-[#25D366] focus:border-[#25D366] outline-none text-xs sm:text-sm"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-base sm:text-lg"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <button
            onClick={handleExportOriginal}
            className="flex items-center justify-center bg-[#25D366] text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium hover:bg-[#1cb85c] shadow-sm transition-all duration-200 hover:shadow-md whitespace-nowrap w-full lg:w-auto mt-2 lg:mt-0"
          >
            <TbDownload className="mr-1 sm:mr-2" /> Download All
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Table Header */}
        <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col xs:flex-row xs:items-center justify-between gap-2 sm:gap-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={
                  selectedContacts.length === filteredContacts.length &&
                  filteredContacts.length > 0
                }
                onChange={toggleSelectAll}
                className="rounded border-gray-300 text-[#25D366] focus:ring-[#25D366] w-4 h-4 sm:w-5 sm:h-5"
              />
              <span className="text-xs sm:text-sm text-gray-600">
                {selectedContacts.length > 0
                  ? `${selectedContacts.length} selected`
                  : `${filteredContacts.length} contacts`}
              </span>
            </div>
            <div className="text-xs sm:text-sm text-gray-500">
              Showing {filteredContacts.length} of {contacts.length} contacts
            </div>
          </div>
        </div>

        {/* Responsive Table Container */}
        <div className="overflow-hidden">
          <div
            className="overflow-x-auto overflow-y-auto"
            style={{
              maxHeight: "calc(100vh - 300px)",
              minHeight: "200px",
            }}
          >
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr className="text-left text-gray-600 font-medium">
                  <th className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 font-semibold w-10 sm:w-12 bg-gray-50"></th>
                  <th className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 font-semibold w-12 sm:w-16 bg-gray-50">
                    #
                  </th>
                  <th className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 font-semibold bg-gray-50 min-w-[120px] sm:min-w-[150px]">
                    Contact Name
                  </th>
                  <th className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 font-semibold bg-gray-50 min-w-[80px] sm:min-w-[100px]">
                    Group
                  </th>
                  <th className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 font-semibold bg-gray-50 min-w-[80px] sm:min-w-[100px]">
                    Tags
                  </th>
                  <th className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 font-semibold bg-gray-50 min-w-[100px] sm:min-w-[120px]">
                    Mobile Number
                  </th>
                  <th className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 font-semibold text-right bg-gray-50 min-w-[80px] sm:min-w-[100px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredContacts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 sm:py-16 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-400">
                        <MdOutlineInbox className="text-4xl sm:text-5xl mb-3 sm:mb-4" />
                        <p className="text-base sm:text-lg font-medium text-gray-500 mb-1 sm:mb-2">
                          {searchQuery
                            ? "No contacts found"
                            : "No contacts available"}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4 text-center px-2">
                          {searchQuery
                            ? "Try adjusting your search criteria"
                            : "Get started by adding your first contact"}
                        </p>
                        {!searchQuery && (
                          <button
                            onClick={() => setIsAddModalOpen(true)}
                            className="flex items-center justify-center bg-[#25D366] hover:bg-[#1cb85c] text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium w-full xs:w-auto"
                          >
                            <FiPlus className="mr-1 sm:mr-2" /> Add Contact
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredContacts.map((contact, index) => (
                    <tr
                      key={contact.id}
                      className="hover:bg-gray-50 transition-colors duration-150 group"
                    >
                      <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={(e) =>
                            toggleSelectContact(contact.id, e.target.checked)
                          }
                          className="rounded border-gray-300 text-[#25D366] focus:ring-[#25D366] w-4 h-4 sm:w-5 sm:h-5"
                        />
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 text-gray-500 font-medium">
                        {index + 1}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">
                        <div className="flex items-center">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-[#25D366] rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-medium mr-2 sm:mr-3">
                            {contact.contact_name?.charAt(0)?.toUpperCase() ||
                              "C"}
                          </div>
                          <span className="font-medium text-gray-900 text-xs sm:text-sm truncate max-w-[80px] sm:max-w-none">
                            {contact.contact_name}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">
                        <span className="inline-flex items-center px-1.5 sm:px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 truncate max-w-[80px] sm:max-w-none">
                          {contact.contact_group || "No Group"}
                        </span>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">
                        {contact.tags ? (
                          <span className="inline-flex items-center px-1.5 sm:px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 truncate max-w-[80px] sm:max-w-none">
                            {contact.tags}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">No tags</span>
                        )}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4 font-mono text-gray-600 text-xs sm:text-sm">
                        {contact.mobile_number}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-3 md:px-4">
                        <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                          <button
                            onClick={() => {
                              if (contact.is_blocked) return;
                              setIsAddModalOpen(true);
                              setEditingContact(contact);
                            }}
                            disabled={contact.is_blocked}
                            className={`p-1 sm:p-2 rounded-lg transition-colors duration-200
    ${
      contact.is_blocked
        ? "text-gray-300 cursor-not-allowed"
        : "text-gray-400 hover:text-[#25D366] hover:bg-green-50"
    }
  `}
                            title={
                              contact.is_blocked
                                ? "Unblock to edit contact"
                                : "Edit Contact"
                            }
                          >
                            <RiEditLine className="text-base sm:text-lg" />
                          </button>

                          <button
                            onClick={() => handleBlockContact(contact)}
                            className={`p-1 sm:p-2 rounded-lg transition-colors duration-200
    ${
      contact.is_blocked
        ? "text-red-600 bg-red-50"
        : "text-gray-400 hover:text-orange-600 hover:bg-orange-50"
    }
  `}
                            title={
                              contact.is_blocked
                                ? "Unblock Contact"
                                : "Block Contact"
                            }
                          >
                            <MdBlock className="text-base sm:text-lg" />
                          </button>

                          <button
                            onClick={() => handleDelete(contact.id)}
                            className="p-1 sm:p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            title="Delete Contact"
                          >
                            <RiDeleteBinLine className="text-base sm:text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl w-[90%] max-w-md">
            <h2 className="text-xl font-semibold mb-2 text-gray-800">
              Delete Contact?
            </h2>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this contact?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteAction}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmBulkDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl w-[90%] max-w-md">
            <h2 className="text-xl font-semibold mb-2 text-gray-800">
              Delete Selected Contacts?
            </h2>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete
              <span className="font-semibold"> {selectedContacts.length} </span>
              selected contact(s)?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmBulkDelete(false)}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDeleteAction}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {isAddModalOpen && (
        <AddContactModal
          onClose={handleCloseAddModal}
          refreshContacts={fetchContacts}
          contactToEdit={editingContact}
          onSuccess={handleContactActionSuccess} // Pass success callback
        />
      )}
      {isImportModalOpen && (
        <ImportContactModal
          onClose={handleCloseImportModal}
          onImport={handleImport}
        />
      )}
    </div>
  );
};

export default Contacts;